/**
 * OnboardingWizard (Plan 11; per familie fra FF-11, #179).
 *
 * Root component of the /onboarding/step-:n route tree.  Systemadminen har
 * opprettet familien og den første adminen; adminen er logget inn med sin
 * egen økt og fullfører oppsettet her: «Om deg» → medlemmene → installer og
 * ferdig.  `GET /api/onboarding/status` avgjør: uten økt går man til
 * innloggingen, og er familien ferdig satt opp (eller man ikke er admin),
 * går man til `/`.
 *
 * Renders a 3-segment progress indicator.  #159 steg 9f: segmentene opp til
 * og med dette trinnet er lime (`bg-fab`), resten `bg-track`, og «Steg N av 3»
 * står ved siden av.  Segmentene er pynt (aria-hidden); teksten er det
 * skjermleseren leser, så fargen er ikke eneste kjennetegn.
 * Lerretet og gløden kommer fra App-roten, så `main` har ingen egen
 * bakgrunn (steg 1).  The step number comes from the `:n` route param so
 * deep-linking works; a full refresh wipes the in-memory member list
 * intentionally so allergy chips aren't cached to disk (T-data-01).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { OnboardingStatus } from '@stell/shared';
import { OnboardingApi, OnboardingApiError } from './api';
import { useOnboardingState, type WizardStep } from './useOnboardingState';
import { StepAboutYou } from './steps/StepAboutYou';
import { StepMember } from './steps/StepMember';
import { StepInstallGuide } from './steps/StepInstallGuide';

const TOTAL_STEPS: WizardStep[] = [1, 2, 3];

// `:step` param is the whole segment, e.g. "step-1".  Strip the literal
// "step-" prefix and coerce to a 1..3 integer, defaulting to 1.
function parseStep(raw: string | undefined): WizardStep {
  if (!raw) return 1;
  const match = /^step-(\d+)$/.exec(raw);
  const n = match ? Number(match[1]) : Number(raw);
  if (n === 1 || n === 2 || n === 3) return n;
  return 1;
}

type Gate = { kind: 'loading' } | { kind: 'open'; status: OnboardingStatus } | { kind: 'closed' | 'login' | 'error' };

export function OnboardingWizard() {
  const { step: stepParam } = useParams<{ step: string }>();
  const step = parseStep(stepParam);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [gate, setGate] = useState<Gate>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    OnboardingApi.status()
      .then((status) => {
        if (cancelled) return;
        if (status.profileConfirmed) useOnboardingState.getState().setProfile({ confirmed: true });
        setGate(status.needsSetup ? { kind: 'open', status } : { kind: 'closed' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setGate({ kind: err instanceof OnboardingApiError && err.status === 401 ? 'login' : 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // useEffect + useNavigate lets the surrounding Router finish its state
  // settle before we push the next location.
  useEffect(() => {
    if (gate.kind === 'closed') navigate('/', { replace: true });
    if (gate.kind === 'login') navigate('/login', { replace: true });
  }, [gate.kind, navigate]);

  // Keep local state.step in sync with URL — helps downstream steps that
  // read `step` straight from the store.
  useEffect(() => {
    useOnboardingState.getState().setStep(step);
  }, [step]);

  const progress = useMemo(
    () =>
      TOTAL_STEPS.map((s) => ({
        step: s,
        active: s === step,
        done: s <= step,
      })),
    [step],
  );
  const progressLabel = t('onboarding.progress', { current: step, total: TOTAL_STEPS.length });

  if (gate.kind === 'loading') {
    return <div data-testid="onboarding-loading" aria-busy="true" />;
  }
  if (gate.kind === 'error') {
    return (
      <div data-testid="onboarding-error" role="alert" className="text-bad p-5">
        {t('onboarding.load_error')}
      </div>
    );
  }
  if (gate.kind !== 'open') return null;

  return (
    <main
      data-testid="onboarding-wizard"
      data-step={step}
      className="mx-auto flex min-h-dvh w-full max-w-lg flex-col gap-5 px-5 pt-14"
    >
      <div className="flex items-center gap-2.5">
        <ol
          data-testid="progress"
          aria-hidden="true"
          className="m-0 flex flex-1 list-none gap-1.5 p-0"
        >
          {progress.map((p) => (
            <li
              key={p.step}
              data-testid={`progress-dot-${p.step}`}
              data-active={p.active}
              data-done={p.done}
              className={`h-1.5 flex-1 rounded-pill ${p.done ? 'bg-fab' : 'bg-track'}`}
            />
          ))}
        </ol>
        <span data-testid="progress-label" className="text-caption font-extrabold text-ink-2">
          {progressLabel}
        </span>
      </div>

      <StepRouter step={step} status={gate.status} />
    </main>
  );
}

function StepRouter({ step, status }: { step: WizardStep; status: OnboardingStatus }) {
  switch (step) {
    case 1:
      return <StepAboutYou status={status} />;
    case 2:
      return <StepMember />;
    case 3:
      return <StepInstallGuide />;
  }
}
